import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder, FolderOpen, FileText, ChevronRight, ChevronDown,
  RotateCcw, Search, Loader2, AlertCircle, Menu, Crosshair, Trash2, X
} from 'lucide-react';

interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
  claudeChanged?: boolean;
  claudeRead?: boolean;
}

interface DirectoryTreeProps {
  serverUrl?: string;
  rootPath?: string;
}

// Known project roots - only these get loaded
const PROJECT_ROOTS = [
  { label: 'PZ Server Dev', path: '/home/forge/pz-server-dev' },
  { label: 'PZRP Core', path: '/home/forge/pzrp-core' },
  { label: 'ControlDeck', path: '/home/forge/mothershipio.ca/shared/controldeck-src' },
  { label: 'Scripts', path: '/home/forge/mothershipio.ca/shared/scripts' },
  { label: 'Claude Tools', path: '/home/forge/.claude' },
  { label: 'Synapse', path: '/home/forge/mothershipio.ca/current/WarpDrive/Synapse' },
];

// Glass panel wrapper - matches ActivityPanel style
const GlassPanel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`relative overflow-hidden ${className}`}>
    <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-xl" />
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5" />
    <div className="absolute inset-0 border-r border-white/10" />
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-50" />
    <div className="relative h-full flex flex-col">{children}</div>
  </div>
);

const TreeNode: React.FC<{
  node: FileNode;
  level: number;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  expandedPaths: Set<string>;
  onToggle: (path: string) => void;
}> = ({ node, level, selectedPath, onSelect, expandedPaths, onToggle }) => {
  const isExpanded = expandedPaths.has(node.path);
  const isSelected = selectedPath === node.path;
  const isDirectory = node.type === 'directory';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(node.path);
    if (isDirectory) {
      onToggle(node.path);
    }
  };

  return (
    <div>
      <div
        onClick={handleClick}
        className={`flex items-center gap-1.5 px-2 py-1 cursor-pointer transition-all text-xs ${
          isSelected
            ? 'bg-cyan-500/20 text-cyan-300 border-l-2 border-cyan-400'
            : 'hover:bg-white/5 text-gray-400'
        } ${node.claudeChanged ? 'bg-amber-500/10' : node.claudeRead ? 'bg-green-500/10' : ''}`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        {/* Expand/collapse arrow for directories */}
        {isDirectory && (
          <span className="text-gray-500 w-3">
            {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        )}
        {!isDirectory && <span className="w-3" />}

        {/* Icon */}
        {isDirectory ? (
          isExpanded ? (
            <FolderOpen className="w-3.5 h-3.5 text-yellow-500/70" />
          ) : (
            <Folder className="w-3.5 h-3.5 text-yellow-500/70" />
          )
        ) : (
          <FileText className={`w-3.5 h-3.5 ${node.claudeChanged ? 'text-amber-400' : node.claudeRead ? 'text-green-400' : 'text-gray-500'}`} />
        )}

        {/* Name */}
        <span className={`truncate ${node.claudeChanged ? 'text-amber-300 font-medium' : node.claudeRead ? 'text-green-300' : ''}`}>
          {node.name}
        </span>

        {/* Claude changed indicator */}
        {node.claudeChanged && (
          <span className="ml-auto text-[9px] text-amber-400 bg-amber-500/20 px-1 rounded">
            edited
          </span>
        )}
        {/* Claude read indicator - only show if not changed */}
        {!node.claudeChanged && node.claudeRead && (
          <span className="ml-auto text-[9px] text-green-400 bg-green-500/20 px-1 rounded">
            read
          </span>
        )}
      </div>

      {/* Children */}
      {isDirectory && isExpanded && node.children && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              level={level + 1}
              selectedPath={selectedPath}
              onSelect={onSelect}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const DirectoryTree: React.FC<DirectoryTreeProps> = ({
  serverUrl = '/api/claude-logs',
  rootPath: initialRootPath
}) => {
  const [currentRoot, setCurrentRoot] = useState(initialRootPath || PROJECT_ROOTS[0].path);
  const [showRootSelector, setShowRootSelector] = useState(false);
  const [tree, setTree] = useState<FileNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [claudeChangedFiles, setClaudeChangedFiles] = useState<Set<string>>(new Set());
  const [claudeReadFiles, setClaudeReadFiles] = useState<Set<string>>(new Set());
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [focusEnabled, setFocusEnabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const currentRootLabel = PROJECT_ROOTS.find(r => r.path === currentRoot)?.label || 'Custom';

  // Fetch directory tree
  const fetchTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${serverUrl}/api/directory-tree?path=${encodeURIComponent(currentRoot)}`);
      if (!res.ok) throw new Error('Failed to fetch directory tree');
      const data = await res.json();
      setTree(data);
      // Auto-expand root
      setExpandedPaths(new Set([currentRoot]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
    setLoading(false);
  }, [serverUrl, currentRoot]);

  // Handle root change
  const handleRootChange = (newRoot: string) => {
    setCurrentRoot(newRoot);
    setShowRootSelector(false);
    setSelectedPath(null);
    setAnalysisResult(null);
  };

  // Fetch Claude-changed files
  const fetchClaudeChanges = useCallback(async () => {
    try {
      const res = await fetch(`${serverUrl}/api/claude-changed-files`);
      if (!res.ok) return;
      const data = await res.json();
      setClaudeChangedFiles(new Set(data.files || []));
    } catch {
      // Silently fail
    }
  }, [serverUrl]);

  // Fetch Claude-read files
  const fetchClaudeReads = useCallback(async () => {
    try {
      const res = await fetch(`${serverUrl}/api/claude-read-files`);
      if (!res.ok) return;
      const data = await res.json();
      setClaudeReadFiles(new Set(data.files || []));
    } catch {
      // Silently fail
    }
  }, [serverUrl]);

  // Reset highlights - also resets server session to clear history
  const resetHighlights = async () => {
    try {
      await fetch(`${serverUrl}/api/reset-session`, { method: 'POST' });
    } catch {}
    setClaudeChangedFiles(new Set());
    setClaudeReadFiles(new Set());
  };

  // Toggle expansion
  const handleToggle = (path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  // Run analysis on selected path
  const runAnalysis = async () => {
    if (!selectedPath) return;
    setAnalyzing(true);
    setAnalysisResult(null);
    try {
      const res = await fetch(`${serverUrl}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: selectedPath })
      });
      if (!res.ok) throw new Error('Analysis failed');
      const data = await res.json();
      setAnalysisResult(data);
    } catch (err) {
      setAnalysisResult({ error: err instanceof Error ? err.message : 'Analysis failed' });
    }
    setAnalyzing(false);
  };

  // Toggle focus context injection
  const toggleFocus = async () => {
    if (!selectedPath) return;
    const newEnabled = !focusEnabled;
    try {
      await fetch(`${serverUrl}/api/focus-context`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: selectedPath, enabled: newEnabled })
      });
      setFocusEnabled(newEnabled);
    } catch (err) {
      console.error('Failed to set focus context:', err);
    }
  };

  // Delete selected path
  const handleDelete = async () => {
    if (!selectedPath) return;
    setDeleting(true);
    try {
      const res = await fetch(`${serverUrl}/api/delete-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetPath: selectedPath })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setShowDeleteModal(false);
      setSelectedPath(null);
      fetchTree(); // Refresh tree
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
    setDeleting(false);
  };

  // Navigate to a file in the tree - expand parents and select
  const navigateToFile = (filePath: string) => {
    // Build list of all parent directories to expand
    const pathParts = filePath.split('/');
    const parentsToExpand: string[] = [];
    let currentPath = '';
    for (let i = 0; i < pathParts.length - 1; i++) {
      currentPath += (i === 0 ? '' : '/') + pathParts[i];
      if (currentPath) parentsToExpand.push(currentPath);
    }

    // Expand all parents
    setExpandedPaths(prev => {
      const next = new Set(prev);
      parentsToExpand.forEach(p => next.add(p));
      return next;
    });

    // Select the file
    setSelectedPath(filePath);
  };

  // Mark nodes as Claude-changed or Claude-read
  const markChangedNodes = (node: FileNode): FileNode => {
    const isChanged = claudeChangedFiles.has(node.path);
    const isRead = claudeReadFiles.has(node.path);
    const children = node.children?.map(markChangedNodes);
    const hasChangedChild = children?.some(c => c.claudeChanged || (c.children?.some(cc => cc.claudeChanged)));
    const hasReadChild = children?.some(c => c.claudeRead || (c.children?.some(cc => cc.claudeRead)));

    return {
      ...node,
      claudeChanged: isChanged || hasChangedChild,
      claudeRead: (isRead || hasReadChild) && !isChanged && !hasChangedChild,
      children
    };
  };

  useEffect(() => {
    fetchTree();
    fetchClaudeChanges();
    fetchClaudeReads();

    // Poll for changes every 30s
    const interval = setInterval(() => {
      fetchClaudeChanges();
      fetchClaudeReads();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTree, fetchClaudeChanges, fetchClaudeReads]);

  const markedTree = tree ? markChangedNodes(tree) : null;

  return (
    <GlassPanel className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 shrink-0 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowRootSelector(!showRootSelector)}
            className="p-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 transition-colors"
            title="Select project"
          >
            <Menu className="w-3.5 h-3.5 text-amber-400" />
          </button>
          <span className="text-xs font-semibold text-white truncate max-w-[100px]">{currentRootLabel}</span>
          {claudeChangedFiles.size > 0 && (
            <span className="text-[10px] text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded">
              {claudeChangedFiles.size}
            </span>
          )}
          {claudeReadFiles.size > 0 && (
            <span className="text-[10px] text-green-400 bg-green-500/20 px-1.5 py-0.5 rounded">
              {claudeReadFiles.size}
            </span>
          )}
        </div>
        <button
          onClick={resetHighlights}
          className="p-1 rounded hover:bg-white/10 text-gray-500 hover:text-gray-300 transition-colors"
          title="Reset highlights"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Project selector dropdown */}
      {showRootSelector && (
        <div className="absolute top-10 left-2 right-2 z-50 bg-gray-900/95 border border-white/10 rounded-lg shadow-xl backdrop-blur-sm">
          {PROJECT_ROOTS.map((root) => (
            <button
              key={root.path}
              onClick={() => handleRootChange(root.path)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors ${
                currentRoot === root.path ? 'text-cyan-400 bg-cyan-500/10' : 'text-gray-300'
              }`}
            >
              {root.label}
            </button>
          ))}
        </div>
      )}

      {/* Tree */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <Loader2 className="w-6 h-6 mb-2 animate-spin" />
            <span className="text-xs">Loading...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-red-400">
            <AlertCircle className="w-6 h-6 mb-2" />
            <span className="text-xs">{error}</span>
          </div>
        ) : markedTree ? (
          <TreeNode
            node={markedTree}
            level={0}
            selectedPath={selectedPath}
            onSelect={setSelectedPath}
            expandedPaths={expandedPaths}
            onToggle={handleToggle}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-600">
            <Folder className="w-6 h-6 mb-2 opacity-30" />
            <span className="text-xs">No files</span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="px-2 py-2 border-t border-white/5 bg-white/[0.02] space-y-2">
        <div className="flex gap-1.5">
          {/* Focus toggle */}
          <button
            onClick={toggleFocus}
            disabled={!selectedPath}
            className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-all ${
              selectedPath
                ? focusEnabled
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-gray-700/50 text-gray-400 hover:bg-gray-600/50 border border-gray-600/30'
                : 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
            }`}
            title="Inject path as context into pre-tool hook"
          >
            <Crosshair className="w-3 h-3" />
            <span>{focusEnabled ? 'Focused' : 'Focus'}</span>
          </button>

          {/* Delete button */}
          <button
            onClick={() => setShowDeleteModal(true)}
            disabled={!selectedPath}
            className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-all ${
              selectedPath
                ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20'
                : 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
            }`}
            title="Delete file or directory"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>

        {/* Analyze button */}
        <button
          onClick={runAnalysis}
          disabled={!selectedPath || analyzing}
          className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium transition-all ${
            selectedPath && !analyzing
              ? 'bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-cyan-300 hover:from-purple-500/30 hover:to-cyan-500/30 border border-cyan-500/30'
              : 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
          }`}
        >
          {analyzing ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <Search className="w-3 h-3" />
              <span>Analyze</span>
            </>
          )}
        </button>

        {selectedPath && (
          <div className="text-[10px] text-gray-500 truncate text-center">
            {selectedPath}
          </div>
        )}
      </div>

      {/* Analysis results */}
      {analysisResult && (
        <div className="px-2 py-2 border-t border-white/5 bg-black/20 max-h-32 overflow-y-auto">
          {analysisResult.error ? (
            <div className="text-xs text-red-400">{analysisResult.error}</div>
          ) : (
            <div className="space-y-1">
              {analysisResult.duplicates?.length > 0 && (
                <div>
                  <div className="text-[10px] text-purple-400 font-medium mb-1">Duplicates:</div>
                  {analysisResult.duplicates.slice(0, 5).map((d: any, i: number) => (
                    <button
                      key={i}
                      onClick={() => navigateToFile(d.file)}
                      className="block w-full text-left text-[10px] text-gray-400 truncate hover:text-purple-300 hover:bg-purple-500/10 px-1 py-0.5 rounded transition-colors cursor-pointer"
                      title={`Navigate to ${d.file}`}
                    >
                      {d.file}: {d.count} occurrences
                    </button>
                  ))}
                </div>
              )}
              {analysisResult.dependencies?.length > 0 && (
                <div>
                  <div className="text-[10px] text-cyan-400 font-medium mb-1">Dependencies:</div>
                  {analysisResult.dependencies.slice(0, 5).map((d: string, i: number) => (
                    <div key={i} className="text-[10px] text-gray-400 truncate">{d}</div>
                  ))}
                </div>
              )}
              {!analysisResult.duplicates?.length && !analysisResult.dependencies?.length && (
                <div className="text-[10px] text-gray-500">No duplicates or dependencies found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedPath && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-red-500/30 rounded-lg p-4 mx-4 max-w-sm shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-red-400">
                <Trash2 className="w-4 h-4" />
                <span className="text-sm font-medium">Confirm Delete</span>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 hover:bg-white/10 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <p className="text-xs text-gray-300 mb-2">
              Are you sure you want to delete:
            </p>
            <div className="text-xs text-red-300 bg-red-500/10 px-2 py-1.5 rounded mb-4 break-all">
              {selectedPath}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-500 rounded transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </GlassPanel>
  );
};

export default DirectoryTree;
